import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { getLoginPageSettings, saveLoginPageSettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';

interface LoginPageSettingsProps {
  companyId: string | null;
  configId: string;
}

export interface LoginPageChromeSettings {
  showHeader: boolean;
  showMenu: boolean;
  showFooter: boolean;
  showBottomNavbar: boolean;
  showHeaderInfoStrip: boolean;
  showHeaderMarqueeStrip: boolean;
  showExchangeRatesBar: boolean;
}

export interface LoginPageDesignSettings {
  welcomeTitle: string;
  welcomeSubtitle: string;
  primaryColor: string;
  accentGradientFrom: string;
  accentGradientTo: string;
  logoUrl: string;
  brandName: string;
  showRememberMe: boolean;
  showRegisterTab: boolean;
  loginButtonText: string;
  promoTitle: string;
  promoDescription: string;
  promoCtaText: string;
  promoCtaUrl: string;
  lottieUrl: string;
  showLottie: boolean;
  promoImageUrl: string;
  legalLinkText: string;
  legalLinkUrl: string;
  contactLinkText: string;
  contactLinkUrl: string;
}

export interface LoginPageSettingsData {
  enabled: boolean;
  templateType: 'default' | 'aquasoft' | 'b2bmotion';
  chrome: LoginPageChromeSettings;
  design: LoginPageDesignSettings;
}

const defaultSettings: LoginPageSettingsData = {
  enabled: true,
  templateType: 'aquasoft',
  chrome: {
    showHeader: false,
    showMenu: false,
    showFooter: false,
    showBottomNavbar: false,
    showHeaderInfoStrip: false,
    showHeaderMarqueeStrip: false,
    showExchangeRatesBar: false,
  },
  design: {
    welcomeTitle: "B2B'ye Hoşgeldiniz",
    welcomeSubtitle: 'İşletmenizi yönetmek için bilgilerinizi girin.',
    primaryColor: '#1e3a5f',
    accentGradientFrom: '#1e3a5f',
    accentGradientTo: '#3b82f6',
    logoUrl: '/assets/Aquasoft_ikon.jpeg',
    brandName: 'AQUASOFT',
    showRememberMe: true,
    showRegisterTab: true,
    loginButtonText: 'GİRİŞ YAP',
    promoTitle: 'Bayimiz Olmak İster Misiniz?',
    promoDescription:
      'AquaSoft ekosistemine katılarak işinizi büyütün. Bayilik başvurunuzu hemen yapın, size özel avantajlardan yararlanın.',
    promoCtaText: 'HEMEN BAŞVURU YAP',
    promoCtaUrl: '/become-seller',
    lottieUrl: '',
    showLottie: false,
    promoImageUrl: '/assets/Aquasoft.png',
    legalLinkText: 'YASAL UYARI',
    legalLinkUrl: '',
    contactLinkText: 'İLETİŞİM',
    contactLinkUrl: '/contact',
  },
};

const LoginPageSettings: React.FC<LoginPageSettingsProps> = ({ companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<LoginPageSettingsData>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await getLoginPageSettings(companyId, configId);
        if (res?.success && res.data) {
          setSettingsId(res.data.id || null);
          let parsed: Partial<LoginPageSettingsData> = {};
          if (res.data.settingsJson) {
            try {
              parsed = JSON.parse(res.data.settingsJson);
            } catch {
              parsed = {};
            }
          }
          setSettings({
            ...defaultSettings,
            enabled: res.data.enabled ?? defaultSettings.enabled,
            templateType: res.data.templateType || defaultSettings.templateType,
            chrome: { ...defaultSettings.chrome, ...(parsed.chrome || {}) },
            design: { ...defaultSettings.design, ...(parsed.design || {}) },
          });
        }
      } catch (err: any) {
        setError(err?.message || t('ecommerceLoginPage.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId, configId, t]);

  const persist = async (next: LoginPageSettingsData) => {
    if (!companyId || !configId) return;
    setSaving(true);
    try {
      const payload = {
        id: settingsId,
        companyId,
        configId,
        enabled: next.enabled,
        templateType: next.templateType,
        settingsJson: JSON.stringify({
          chrome: next.chrome,
          design: next.design,
        }),
      };
      const res = await saveLoginPageSettings(payload);
      if (res?.success && res.data?.id) {
        setSettingsId(res.data.id);
      }
    } catch (err: any) {
      setError(err?.message || t('ecommerceLoginPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const scheduleSave = (next: LoginPageSettingsData) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(next), 600);
  };

  const updateSettings = (partial: Partial<LoginPageSettingsData>) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    scheduleSave(next);
  };

  const updateChrome = (key: keyof LoginPageChromeSettings, value: boolean) => {
    const next = { ...settings, chrome: { ...settings.chrome, [key]: value } };
    setSettings(next);
    scheduleSave(next);
  };

  const updateDesign = (key: keyof LoginPageDesignSettings, value: string | boolean) => {
    const next = { ...settings, design: { ...settings.design, [key]: value } };
    setSettings(next);
    scheduleSave(next);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LoginIcon /> {t('ecommerceLoginPage.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('ecommerceLoginPage.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saving && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('ecommerceLoginPage.saving')}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('ecommerceLoginPage.generalTitle')}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => updateSettings({ enabled: e.target.checked })}
                />
              }
              label={t('ecommerceLoginPage.enabled')}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t('ecommerceLoginPage.templateType')}</InputLabel>
              <Select
                value={settings.templateType}
                label={t('ecommerceLoginPage.templateType')}
                onChange={(e) =>
                  updateSettings({ templateType: e.target.value as LoginPageSettingsData['templateType'] })
                }
              >
                <MenuItem value="default">{t('ecommerceLoginPage.templateDefault')}</MenuItem>
                <MenuItem value="aquasoft">{t('ecommerceLoginPage.templateAquasoft')}</MenuItem>
                <MenuItem value="b2bmotion">{t('ecommerceLoginPage.templateB2BMotion')}</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('ecommerceLoginPage.chromeTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ecommerceLoginPage.chromeDescription')}
            </Typography>

            {(
              [
                ['showHeader', 'ecommerceLoginPage.showHeader'],
                ['showMenu', 'ecommerceLoginPage.showMenu'],
                ['showFooter', 'ecommerceLoginPage.showFooter'],
                ['showBottomNavbar', 'ecommerceLoginPage.showBottomNavbar'],
                ['showHeaderInfoStrip', 'ecommerceLoginPage.showHeaderInfoStrip'],
                ['showHeaderMarqueeStrip', 'ecommerceLoginPage.showHeaderMarqueeStrip'],
                ['showExchangeRatesBar', 'ecommerceLoginPage.showExchangeRatesBar'],
              ] as const
            ).map(([key, labelKey]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={settings.chrome[key]}
                    onChange={(e) => updateChrome(key, e.target.checked)}
                  />
                }
                label={t(labelKey)}
                sx={{ display: 'block', mb: 0.5 }}
              />
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('ecommerceLoginPage.designTitle')}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.design.showRegisterTab}
                  onChange={(e) => updateDesign('showRegisterTab', e.target.checked)}
                />
              }
              label={t('ecommerceLoginPage.showRegisterTab')}
              sx={{ display: 'block', mb: 1 }}
            />

            <TextField
              fullWidth
              label={t('ecommerceLoginPage.welcomeTitle')}
              value={settings.design.welcomeTitle}
              onChange={(e) => updateDesign('welcomeTitle', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('ecommerceLoginPage.welcomeSubtitle')}
              value={settings.design.welcomeSubtitle}
              onChange={(e) => updateDesign('welcomeSubtitle', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('ecommerceLoginPage.primaryColor')}
              value={settings.design.primaryColor}
              onChange={(e) => updateDesign('primaryColor', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('ecommerceLoginPage.lottieUrl')}
              value={settings.design.lottieUrl}
              onChange={(e) => updateDesign('lottieUrl', e.target.value)}
              sx={{ mb: 2 }}
              helperText={t('ecommerceLoginPage.lottieUrlHelp')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.design.showLottie}
                  onChange={(e) => updateDesign('showLottie', e.target.checked)}
                />
              }
              label={t('ecommerceLoginPage.showLottie')}
            />
            <TextField
              fullWidth
              label={t('ecommerceLoginPage.promoTitle')}
              value={settings.design.promoTitle}
              onChange={(e) => updateDesign('promoTitle', e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('ecommerceLoginPage.promoDescription')}
              value={settings.design.promoDescription}
              onChange={(e) => updateDesign('promoDescription', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Paper>

          <Card sx={{ mt: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                minHeight: 220,
                background: `linear-gradient(135deg, ${settings.design.accentGradientFrom}, ${settings.design.accentGradientTo})`,
              }}
            >
              <Box sx={{ flex: 1, bgcolor: '#fff', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} color={settings.design.primaryColor}>
                  {settings.design.welcomeTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings.design.welcomeSubtitle}
                </Typography>
                <Box sx={{ mt: 2, height: 28, bgcolor: '#f3f4f6', borderRadius: 1 }} />
                <Box sx={{ mt: 1, height: 28, bgcolor: '#f3f4f6', borderRadius: 1 }} />
                <Box
                  sx={{
                    mt: 2,
                    height: 32,
                    bgcolor: settings.design.primaryColor,
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, p: 2, color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {settings.design.promoTitle}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, mt: 1 }}>
                  {settings.design.promoDescription}
                </Typography>
              </Box>
            </Box>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {t('ecommerceLoginPage.previewCaption')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPageSettings;
